import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { DsBannerState, DsButtonIntent, DsButtonVariants, DsInputStates, Icons } from '@levelaccess/design-system';

import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { FormService } from '../../services/form.service';
import { TranslateService } from '../../translate/translate.service';
import { $page, $pageImage, PAGE_CHAR_LIMITS } from '../../../../shared/constants/page';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { validationError } from '../../constants/form.constants';

export interface IPageFormValues {
  title: string;
  url: string;
  description: string;
  altText: string;
  screenshotFile: File | null;
  screenshotOrigin: ScreenshotOrigin | null;
}
import { BannerType } from '../users/edit-user/edit-permissions-banner/edit-permissions-banner.component';
import {
  FileDragAndDropComponent,
  IFileDragAndDropContext,
} from '../form-field/file-upload/file-drag-and-drop/file-drag-and-drop.component';
import { config } from '../../../environments/config.shared';
import { SupportedPageScreenshotExtensions, SupportedPageScreenshotMimeTypes } from '../../../../shared/constants/mime-type';

export enum ScreenshotOrigin {
  NEW = 'NEW',
  UPLOADED = 'UPLOADED',
}

export enum FormField {
  TITLE = 'title',
  URL = 'url',
  DESCRIPTION = 'description',
  SCREENSHOT = 'screenshot',
  ALT_TEXT = 'alt_text',
}

export interface IPageFormConfig {
  requireUrl: boolean;
  urlEditable: boolean;
  initialTitle?: string;
  initialUrl?: string;
  initialDescription?: string;
  initialScreenshotResourceId?: string;
  initialScreenshotAltText?: string;
}

@Component({
  selector: 'app-page-form',
  templateUrl: './page-form.component.html',
  styleUrls: ['./page-form.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFormComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild(FileDragAndDropComponent)
  public fileDragAndDropComponent: FileDragAndDropComponent;

  @Input() config: IPageFormConfig;
  @Input() showUrl: boolean = true;
  @Input() bannerText: string | undefined;

  @Input() existingUrls: string[];
  @Input() existingNames: string[];
  @Input() nameAsyncValidator: AsyncValidatorFn;
  @Input() domainRestrictions: RegExp[];

  public readonly DsBannerState: typeof DsBannerState = DsBannerState;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsButtonIntent: typeof DsButtonIntent = DsButtonIntent;
  public readonly Icons: typeof Icons = Icons;
  public readonly BannerType: typeof BannerType = BannerType;
  public readonly FormField: typeof FormField = FormField;
  public readonly ScreenshotOrigin: typeof ScreenshotOrigin = ScreenshotOrigin;

  public readonly titleCharLimit: number = PAGE_CHAR_LIMITS[$page.name];
  public readonly urlCharLimit: number = PAGE_CHAR_LIMITS[$page.url];
  public readonly descriptionCharLimit: number = PAGE_CHAR_LIMITS[$page.description];
  public readonly altTextCharLimit: number = PAGE_CHAR_LIMITS[$pageImage.altText];

  public form: FormGroup;
  public showUrlWarning: boolean = false;
  public urlError$: Observable<string>;
  public titleError$: Observable<string>;
  public descriptionError$: Observable<string>;
  public altTextError$: Observable<string>;
  public formValidationRequest$: Subject<void> = new Subject<void>();

  public screenshotOrigin: ScreenshotOrigin | null = null;
  public screenshotData: string | ArrayBuffer = null;
  public screenshotFile: File = null;

  private subscription: Subscription = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private formService: FormService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  public get fileUploadContext(): IFileDragAndDropContext {
    return {
      field: FormField.SCREENSHOT,
      maxTotalFileQuantity: config.pageScreenshot.maxUploadQuantity,
      maxFileSize: config.pageScreenshot.maxFileUploadSize,
      maxTotalFileSize: config.pageScreenshot.maxUploadSize,
      acceptedFileExtensions: SupportedPageScreenshotExtensions,
      acceptedMimeTypes: SupportedPageScreenshotMimeTypes,
    };
  }

  public get urlFieldState(): DsInputStates {
    return this.config?.urlEditable ? DsInputStates.active : DsInputStates.nonEditable;
  }

  public ngOnInit(): void {
    this.buildForm();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) {
      return;
    }

    if (changes.config && !changes.config.firstChange) {
      this.applyConfigToForm();
    }

    if (changes.existingUrls && !changes.existingUrls.firstChange && this.existingUrls) {
      this.addDuplicateUrlWarningSubscription();
    }
    if (changes.existingNames && !changes.existingNames.firstChange && this.existingNames) {
      const existingNameSet: Set<string> = new Set(this.existingNames.map((n: string) => n.toLowerCase()));
      this.form.get(FormField.TITLE).addValidators(CustomValidators.uniqueValidator(existingNameSet, true, true));
    }
    if (changes.nameAsyncValidator && !changes.nameAsyncValidator.firstChange && this.nameAsyncValidator) {
      this.form.get(FormField.TITLE).addAsyncValidators(this.nameAsyncValidator);
    }
    if (changes.domainRestrictions && !changes.domainRestrictions.firstChange && this.domainRestrictions) {
      this.form.get(FormField.URL).addValidators(CustomValidators.domainRestrictionsValidator(this.domainRestrictions));
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public triggerValidation(): boolean {
    this.formValidationRequest$.next();
    Object.values(this.form.controls).forEach((control: AbstractControl) => {
      control.markAsTouched();
      control.markAsDirty();
      control.setErrors(control.errors);
    });
    return this.form.valid;
  }

  public getValues(): IPageFormValues {
    return {
      title: this.form.get(FormField.TITLE).value?.trim() ?? '',
      url: this.form.get(FormField.URL).value?.trim() ?? '',
      description: this.form.get(FormField.DESCRIPTION).value?.trim() ?? '',
      altText: this.form.get(FormField.ALT_TEXT).value?.trim() ?? '',
      screenshotFile: this.screenshotFile,
      screenshotOrigin: this.screenshotOrigin,
    };
  }

  public getFormData(): FormData {
    const values: IPageFormValues = this.getValues();
    const formData: FormData = new FormData();
    formData.set($page.name, values.title);

    if (SharedCommonUtility.notNullishOrEmpty(values.url)) {
      formData.set($page.url, values.url);
    }
    if (SharedCommonUtility.notNullishOrEmpty(values.description)) {
      formData.set($page.description, values.description);
    }
    if (SharedCommonUtility.notNullish(values.screenshotFile)) {
      formData.set($page.screenshot, values.screenshotFile, values.screenshotFile.name);
    }
    if (SharedCommonUtility.notNullishOrEmpty(values.altText)) {
      formData.set($pageImage.altText, values.altText);
    }

    return formData;
  }

  public isFormPristine(): boolean {
    return this.form.pristine;
  }

  public onFileUpload(files: File[]): void {
    if (files.length === 0 || SharedCommonUtility.isNullish(files[0])) {
      return;
    }

    this.screenshotFile = files[0];

    const reader: FileReader = new FileReader();
    reader.onload = (): void => {
      this.screenshotData = reader.result;
      this.screenshotOrigin = ScreenshotOrigin.NEW;
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(this.screenshotFile);

    const control: AbstractControl = this.form.get(FormField.ALT_TEXT);
    control.addValidators(this.altTextValidators());
    control.updateValueAndValidity();
    this.form.markAsDirty();
  }

  public removeScreenshot(): void {
    this.screenshotFile = null;
    this.screenshotData = null;
    this.screenshotOrigin = null;

    this.form.get(FormField.SCREENSHOT).reset();
    this.form.get(FormField.ALT_TEXT).reset('');

    const control: AbstractControl = this.form.get(FormField.ALT_TEXT);
    control.removeValidators(this.altTextValidators());
    control.updateValueAndValidity();
    this.form.markAsDirty();
  }

  public hideUrlWarning(): void {
    this.showUrlWarning = false;
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      [FormField.TITLE]: this.buildTitleFormControl(this.config?.initialTitle),
      [FormField.URL]: this.buildUrlFormControl(this.config?.initialUrl),
      [FormField.DESCRIPTION]: this.buildDescriptionFormControl(this.config?.initialDescription),
      [FormField.SCREENSHOT]: this.formBuilder.control(null),
      [FormField.ALT_TEXT]: this.buildAltTextFormControl(),
    });

    if (this.config?.initialScreenshotResourceId) {
      this.screenshotData = this.config.initialScreenshotResourceId;
      this.screenshotOrigin = ScreenshotOrigin.UPLOADED;

      if (this.config.initialScreenshotAltText) {
        this.form.get(FormField.ALT_TEXT).patchValue(this.config.initialScreenshotAltText);
      }
      this.form.get(FormField.ALT_TEXT).addValidators(this.altTextValidators());
    }

    if (!this.config?.urlEditable) {
      this.form.get(FormField.URL).disable();
    }

    if (this.existingUrls) {
      this.addDuplicateUrlWarningSubscription();
    }
    if (this.existingNames) {
      const existingNameSet: Set<string> = new Set(this.existingNames.map((n: string) => n.toLowerCase()));
      this.form.get(FormField.TITLE).addValidators(CustomValidators.uniqueValidator(existingNameSet, true, true));
    }
    if (this.nameAsyncValidator) {
      this.form.get(FormField.TITLE).addAsyncValidators(this.nameAsyncValidator);
    }
    if (this.domainRestrictions) {
      this.form.get(FormField.URL).addValidators(CustomValidators.domainRestrictionsValidator(this.domainRestrictions));
    }
  }

  private applyConfigToForm(): void {
    this.form.patchValue(
      {
        [FormField.TITLE]: this.config.initialTitle ?? '',
        [FormField.URL]: this.config.initialUrl ?? '',
        [FormField.DESCRIPTION]: this.config.initialDescription ?? '',
      },
      { emitEvent: false },
    );
    this.form.markAsPristine();

    const urlControl: AbstractControl = this.form.get(FormField.URL);
    if (this.config.requireUrl) {
      urlControl.addValidators(CustomValidators.validateIsEmpty);
    } else {
      urlControl.removeValidators(CustomValidators.validateIsEmpty);
    }

    if (!this.config.urlEditable) {
      urlControl.disable();
    } else {
      urlControl.enable();
    }
    urlControl.updateValueAndValidity();

    if (this.config.initialScreenshotResourceId) {
      this.screenshotData = this.config.initialScreenshotResourceId;
      this.screenshotOrigin = ScreenshotOrigin.UPLOADED;

      if (this.config.initialScreenshotAltText) {
        this.form.get(FormField.ALT_TEXT).patchValue(this.config.initialScreenshotAltText);
      }
      this.form.get(FormField.ALT_TEXT).addValidators(this.altTextValidators());
    }
  }

  private addDuplicateUrlWarningSubscription(): void {
    const existingUrlSet: Set<string> = new Set(
      this.existingUrls.filter(SharedCommonUtility.notNullishOrEmpty).map((url: string) => url.toLowerCase()),
    );

    this.subscription.add(
      this.form
        .get(FormField.URL)
        .valueChanges.pipe(
          filter(SharedCommonUtility.notNullishOrEmpty),
          map((url: string) => existingUrlSet.has(url.toLowerCase().trim())),
        )
        .subscribe((show: boolean) => {
          this.showUrlWarning = show;
        }),
    );
  }

  private altTextValidators(): ValidatorFn[] {
    return [CustomValidators.required, CustomValidators.validateIsEmpty, CustomValidators.maxLength(this.altTextCharLimit)];
  }

  private buildTitleFormControl(defaultValue?: string): FormControl {
    const control: FormControl = this.formBuilder.control(defaultValue ?? '', [
      CustomValidators.validateIsEmpty,
      CustomValidators.maxLength(this.titleCharLimit),
    ]);

    this.titleError$ = control.statusChanges.pipe(
      map(() =>
        this.formService.getErrorMessageForField(control, FormField.TITLE, '', '', {
          [validationError.unique]: this.translateService.instant('upsert_page_duplicate_name_error'),
        }),
      ),
    );

    return control;
  }

  private buildUrlFormControl(defaultValue?: string): FormControl {
    const control: FormControl = this.formBuilder.control(defaultValue ?? '', [
      ...(this.config?.requireUrl ? [CustomValidators.validateIsEmpty] : []),
      CustomValidators.isValidUrl,
      CustomValidators.maxLength(this.urlCharLimit),
    ]);

    this.urlError$ = control.statusChanges.pipe(
      map(() =>
        this.formService.getErrorMessageForField(control, FormField.URL, '', '', {
          isDomainPartOfSubscription: this.translateService.instant('url_not_licensed_domain'),
        }),
      ),
    );

    return control;
  }

  private buildDescriptionFormControl(defaultValue?: string): FormControl {
    const control: FormControl = this.formBuilder.control(defaultValue ?? '', [
      CustomValidators.maxLength(this.descriptionCharLimit),
    ]);

    this.descriptionError$ = control.statusChanges.pipe(
      map(() => this.formService.getErrorMessageForField(control, FormField.DESCRIPTION)),
    );

    return control;
  }

  private buildAltTextFormControl(): FormControl {
    const control: FormControl = this.formBuilder.control('', [CustomValidators.maxLength(this.altTextCharLimit)]);

    this.altTextError$ = control.statusChanges.pipe(
      map(() => this.formService.getErrorMessageForField(control, FormField.ALT_TEXT, '', 'upsert_page_alt_text')),
    );

    return control;
  }
}
